import CustomSelect from "@components/compareTariff/customSelect";
import { compareTariffApis } from "@components/compareTariff/enum";
import { unifiedIsoCountries } from "@core/dictionary/global";
import { captureException } from "@sentry/react";
import { getClassifierApiUrl } from "@utils/baseUrl";
import axios from "axios";
import moment from "moment";
import ReactCountryFlag from "react-country-flag";
import { ChevronDown, ChevronUp } from "react-feather";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { ERROR_MSG } from "../../../utils/enum";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import { generateTrackingObject } from "@core/global/global-country-functions";
import { Tooltip } from "react-tooltip5";
import ExpandedViewModal from "@components/compareTariff/expandedViewModal";

const MAX_EXPORT_COUNTRIES = 3;

const STEPS = {
  SELECT_IMPORT_COUNTRY: "select-import-country",
  SELECT_HS: "select-hs",
  SELECT_COUNTRY: "select-country",
  RESULTS: "results",
};

const SECTION_KEYS = ["general_duty", "preferential_duty_rate", "other_taxes", "vat_rates"];

const SECTION_LABELS = {
  general_duty: "General/MFN Duty",
  preferential_duty_rate: "Preferential Duty",
  other_taxes: "Other Duties/ Taxes",
  vat_rates: "VAT/GST Taxes",
};

const SECTION_EMPTY_MESSAGES = {
  general_duty: "No general or MFN duty is applicable.",
  preferential_duty_rate: "No FTA or preferential duty is applicable.",
  other_taxes: "No other duty or tax is applicable.",
  vat_rates: "No VAT or GST is applicable.",
};

const CountryTariffCard = ({ countryCode, countryData, exportLabel }) => {
  const [openSections, setOpenSections] = useState({ general_duty: true });
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const countryName = unifiedIsoCountries[countryCode] || countryCode;

  const toggleSection = (key) => setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleOpenModal = (data, type) => {
    setModalData({ ...data, modalType: type });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalData(null);
  };

  const getRows = (key) => (countryData[key] || []).filter((r) => (r.duty_name && r.duty_name !== "-") || (r.preferential_program && r.preferential_program !== "-"));

  return (
    <div className="tw-w-full tw-bg-white tw-rounded-2xl tw-border tw-border-gray-200 tw-flex tw-flex-col tw-shadow-searchBar">
      {/* Card header */}
      <div className="tw-p-6 tw-border-b tw-border-gray-200">
        <div className="tw-flex tw-items-center tw-gap-3 tw-mb-2">
          <div className="tw-w-12 tw-h-12 tw-flex tw-items-center tw-justify-center tw-bg-white tw-rounded-xl tw-flex-shrink-0" style={{ border: "0.5px solid #eaecf0", boxShadow: "0 2px 4px 0 rgba(234,234,234,0.5)" }}>
            <ReactCountryFlag countryCode={countryCode} svg style={{ width: "20px", height: "15px", borderRadius: "1.5px", boxShadow: "0 2px 3px rgba(0,0,0,0.1)" }} />
          </div>
          <div className="tw-text-xl tw-font-bold tw-text-gray-900" style={{ letterSpacing: "-0.1px" }}>
            {countryName}
            <span className="tw-flex tw-items-center tw-gap-0.5">
              <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="#f04438">
                <path d="M480-480q33 0 56.5-23.5T560-560q0-33-23.5-56.5T480-640q-33 0-56.5 23.5T400-560q0 33 23.5 56.5T480-480Zm0 294q122-112 181-203.5T720-552q0-109-69.5-178.5T480-800q-101 0-170.5 69.5T240-552q0 71 59 162.5T480-186Zm0 106Q319-217 239.5-334.5T160-552q0-150 96.5-239T480-880q127 0 223.5 89T800-552q0 100-79.5 217.5T480-80Zm0-480Z" />
              </svg>
              <span className="tw-text-xs tw-font-semibold tw-text-gray-400">{exportLabel}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Column headers */}
      <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-pt-4 tw-pb-3 tw-border-b tw-border-gray-200 tw-mx-4">
        <span className="tw-text-[10px] tw-font-medium tw-text-gray-400 tw-uppercase tw-tracking-wider">Duty Name</span>
        <span className="tw-text-[10px] tw-font-medium tw-text-gray-400 tw-uppercase tw-tracking-wider">Duty Rate</span>
      </div>

      {/* Accordion sections */}
      <div className="tw-flex-1 tw-overflow-auto tw-px-4 tw-pb-4 tw-space-y-4 tw-pt-4">
        {SECTION_KEYS.map((key) => {
          const rows = getRows(key);
          const isOpen = !!openSections[key];
          return (
            <div key={key} className="tw-rounded-lg tw-overflow-hidden tw-shadow-classifier">
              <button type="button" className="tw-w-full tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-bg-white hover:tw-bg-gray-50 tw-transition" onClick={() => toggleSection(key)}>
                <div className="tw-flex tw-items-center tw-gap-2">
                  {/* Glowing purple dot */}
                  <span className="tw-relative tw-flex-shrink-0 tw-w-2 tw-h-2 tw-flex tw-items-center tw-justify-center">
                    <span className="tw-absolute tw-inset-[-3px] tw-rounded-full tw-bg-primary-purple-100" style={{ filter: "blur(4px)" }} />
                    <span className="tw-relative tw-w-1 tw-h-1 tw-rounded-full tw-bg-primary-purple-500" />
                  </span>
                  <span className="tw-text-sm tw-font-medium tw-text-gray-900 tw-text-left">{SECTION_LABELS[key]}</span>
                </div>
                {isOpen ? <ChevronUp size={16} className="tw-text-gray-400 tw-flex-shrink-0" /> : <ChevronDown size={16} className="tw-text-gray-400 tw-flex-shrink-0" />}
              </button>
              {isOpen && (
                <>
                  <div className="tw-h-px tw-bg-gray-200" />
                  <div className="tw-px-4 tw-pb-2 tw-bg-white tw-space-y-2 tw-py-2">
                    {rows.length > 0 ? (
                      rows.map((row) => (
                        <div key={`${row.duty_name || row.preferential_program}-${row.duty_rate}`} className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                          <span className="tw-text-sm tw-text-gray-900 tw-flex-1 tw-pr-2">{row.duty_name || row.preferential_program}</span>
                          {row.is_condition_data ? (
                            <button type="button" className="tw-text-sm tw-font-medium tw-text-primary-purple-600 tw-underline tw-underline-offset-2 tw-bg-transparent tw-flex-shrink-0 hover:tw-text-primary-purple-800 tw-flex tw-items-center tw-gap-1" onClick={() => handleOpenModal(row.condition_data, "conditions")}>
                              Check Duties
                              <svg xmlns="http://www.w3.org/2000/svg" height="14px" viewBox="0 -960 960 960" width="14px" fill="currentColor">
                                <path d="m256-240-56-56 384-384H240v-80h480v480h-80v-344L256-240Z" />
                              </svg>
                            </button>
                          ) : (
                            <span className="tw-text-sm tw-font-semibold tw-text-warning-600 tw-bg-warning-50 tw-rounded tw-px-1.5 tw-py-0.5 tw-flex-shrink-0">{row.duty_rate}</span>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="tw-text-xs tw-text-gray-400 tw-py-2.5">{SECTION_EMPTY_MESSAGES[key]}</div>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
      <ExpandedViewModal show={showModal} onHide={handleCloseModal} modalData={modalData} />
    </div>
  );
};

const AddCountryCard = ({ onClick, hsCode }) => (
  <div className="tw-w-full tw-rounded-2xl tw-border-2 tw-border-primary-purple-300 tw-border-dashed tw-flex tw-flex-col tw-items-center tw-justify-center tw-cursor-pointer tw-gap-4 tw-p-6 tw-transition-all hover:tw-shadow-md" onClick={onClick}>
    <div className="tw-w-16 tw-h-16 tw-rounded-full tw-bg-white tw-border tw-border-gray-200 tw-shadow-button tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
      <svg xmlns="http://www.w3.org/2000/svg" height="14" width="14" viewBox="0 0 14 14" fill="none">
        <path d="M7 1v12M1 7h12" stroke="#7143f1" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    </div>
    <div className="tw-text-center">
      <div className="tw-text-sm tw-font-medium tw-text-gray-1000 tw-mb-1">Add another country</div>
      {hsCode && <div className="tw-text-sm tw-text-gray-400">Compare more markets for {hsCode}</div>}
    </div>
  </div>
);

const createCountryList = (data) => {
  if (!data) return [];
  return Object.entries(data).map(([code, name]) => ({ value: code, label: name }));
};

const buildKeyword = (searchedState) => {
  if (!searchedState?.search?.length) return "";
  return searchedState.search.map((s) => s.label).join(", ");
};

const ProductTariffs = ({ searchedState, suggestedHS, hsnClassifierLoading, hsnClassifierError, defaultDateRange, currentUserPlan }) => {
  const tradeDirection = searchedState?.tradeDirection?.value || "import";
  const isImport = tradeDirection === "import";

  const [step, setStep] = useState(isImport ? STEPS.SELECT_IMPORT_COUNTRY : STEPS.SELECT_HS);
  const [hsCodes, setHsCodes] = useState([]);
  const [selectedHs, setSelectedHs] = useState(null);
  const [hsLoading, setHsLoading] = useState(false);
  const [hasFetchedHs, setHasFetchedHs] = useState(false);

  const [countryOptions, setCountryOptions] = useState({ countryOfImport: [], countryOfExport: [] });
  const [selectedImportCountry, setSelectedImportCountry] = useState(null);
  const [selectedExportCountries, setSelectedExportCountries] = useState([]);

  const [topCountries, setTopCountries] = useState([]);
  const [topCountriesLoading, setTopCountriesLoading] = useState(false);
  const [userCountryCode, setUserCountryCode] = useState(null);

  const [dutiesData, setDutiesData] = useState(null);
  const [dutiesLoading, setDutiesLoading] = useState(false);

  const keyword = useMemo(() => buildKeyword(searchedState), [searchedState]);

  const dutiableCountry = isImport ? selectedImportCountry?.value : selectedImportCountry?.value || userCountryCode;

  useEffect(() => {
    if (!suggestedHS) return;
    if (!dutiableCountry) return;
    let cancelled = false;
    const fetchDutiableExtension = async () => {
      setHsLoading(true);
      try {
        const payload = {
          product: keyword,
          hscode: suggestedHS,
          country: dutiableCountry,
        };
        const { data } = await axios.post(getClassifierApiUrl("api/v1/hs_dutiable_extension"), payload);
        if (cancelled) return;
        const dutiable = data?.dutiable_hs_codes || [];
        const mapped = dutiable.map((d) => ({ code: d.hs_code, description: d.description || d.reason || "", reason: d.reason }));
        setHsCodes(mapped);
      } catch (err) {
        if (!cancelled) captureException(err);
      } finally {
        if (!cancelled) {
          setHsLoading(false);
          setHasFetchedHs(true);
        }
      }
    };
    fetchDutiableExtension();
    return () => {
      cancelled = true;
    };
  }, [keyword, suggestedHS, dutiableCountry]);

  useEffect(() => {
    let cancelled = false;
    const loadCountries = async () => {
      try {
        const { data } = await axios.get(compareTariffApis.getcountries, { params: { tradeDirection: "import" } });
        if (cancelled) return;
        setCountryOptions({
          countryOfImport: createCountryList(data?.countryOfImport),
          countryOfExport: createCountryList(data?.countryOfExport),
        });
      } catch (err) {
        if (!cancelled) {
          captureException(err);
        }
      }
    };
    loadCountries();
    return () => {
      cancelled = true;
    };
  }, [tradeDirection]);

  useEffect(() => {
    if (!suggestedHS) return;
    let cancelled = false;
    const loadTopCountries = async () => {
      setTopCountriesLoading(true);
      try {
        const { data } = await axios.post(compareTariffApis.getHsCodeTopCountries, {
          hscode: suggestedHS,
          country_type: "export",
          from_date: defaultDateRange?.startDate || "",
          to_date: defaultDateRange?.endDate || "",
        });
        if (cancelled) return;
        setTopCountries(data?.top_countries || []);
        setUserCountryCode(data?.user_country_code || null);
      } catch (err) {
        if (!cancelled) captureException(err);
      } finally {
        if (!cancelled) setTopCountriesLoading(false);
      }
    };
    loadTopCountries();
    return () => {
      cancelled = true;
    };
  }, [suggestedHS, tradeDirection, defaultDateRange?.startDate, defaultDateRange?.endDate]);

  // For export: auto-set buyer's country (country of import) from the API response
  useEffect(() => {
    if (isImport) return;
    if (!userCountryCode) return;
    const option = countryOptions.countryOfImport.find((c) => c.value === userCountryCode);
    setSelectedImportCountry(option || { value: userCountryCode, label: userCountryCode });
  }, [isImport, userCountryCode, countryOptions.countryOfImport]);

  // Prefill Country of Export with top countries (up to MAX_EXPORT_COUNTRIES), excluding import country
  useEffect(() => {
    if (!topCountries.length || !countryOptions.countryOfExport.length) return;
    setSelectedExportCountries((prev) => {
      if (prev.length > 0) return prev;
      const prefilled = [];
      for (const tc of topCountries) {
        if (prefilled.length >= MAX_EXPORT_COUNTRIES) break;
        if (tc.country_code === selectedImportCountry?.value) continue;
        const match = countryOptions.countryOfExport.find((c) => c.value === tc.country_code);
        if (match) prefilled.push(match);
      }
      return prefilled;
    });
  }, [topCountries, countryOptions.countryOfExport, selectedImportCountry?.value]);

  const handleSelectImportCountry = (country) => {
    setSelectedImportCountry(country);
    setSelectedExportCountries((prev) => prev.filter((c) => c.value !== country?.value));
    setHsCodes([]);
    setSelectedHs(null);
    setHasFetchedHs(false);
    setStep(STEPS.SELECT_HS);
    trademoTrackingEvents(trackingEventDictionary.newMay2026ProductSearchTariffsImportCountrySelected, { ...generateTrackingObject("product-search", window.location.pathname, currentUserPlan), country: country?.value });
  };

  const handleSelectHs = (hs) => {
    setSelectedHs(hs);
    setStep(STEPS.SELECT_COUNTRY);
    trademoTrackingEvents(trackingEventDictionary.newMay2026ProductSearchTariffsHsSelected, { ...generateTrackingObject("product-search", window.location.pathname, currentUserPlan), hsCode: hs?.code });
  };

  const goToImportCountryStep = () => {
    setStep(STEPS.SELECT_IMPORT_COUNTRY);
    setHsCodes([]);
    setSelectedHs(null);
    setDutiesData(null);
  };

  const goToCountryStep = () => {
    setStep(STEPS.SELECT_COUNTRY);
    setDutiesData(null);
  };

  const fetchDuties = async () => {
    if (!selectedHs) return;

    const countryOfImport = selectedImportCountry?.value;
    const destination = selectedExportCountries.map((c) => c.value);

    if (!countryOfImport || destination.length === 0) {
      toast.error(isImport ? "Please select country of import and at least one export country" : "Please select country of export");
      return;
    }

    try {
      setDutiesLoading(true);
      const payload = {
        hscode: selectedHs.code,
        trade_direction: "import",
        effectiveDate: moment().format("YYYY-MM-DD"),
        countryOfImport,
        destination,
      };
      const { data } = await axios.post(compareTariffApis.getduties, payload);
      setDutiesData(data?.results || null);
      setStep(STEPS.RESULTS);
      trademoTrackingEvents(trackingEventDictionary.newMay2026ProductSearchTariffsConfirmSelection, { ...generateTrackingObject("product-search", window.location.pathname, currentUserPlan), hsCode: selectedHs?.code, countryOfImport, destination });
    } catch (err) {
      captureException(err);
      toast.error(err?.response?.data?.message || ERROR_MSG);
    } finally {
      setDutiesLoading(false);
    }
  };

  const renderImportCountryHeader = () => {
    if (isImport) {
      return (
        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4 tw-text-sm">
          <span className="tw-text-gray-500">Importing into:</span>
          <span className="tw-font-medium tw-text-gray-900 tw-flex tw-items-center tw-gap-1">
            <ReactCountryFlag countryCode={selectedImportCountry?.value?.slice(0, 2).toUpperCase()} svg />
            {selectedImportCountry?.label}
          </span>
          <button onClick={goToImportCountryStep} className="tw-text-xs tw-text-primary-purple-600 tw-underline tw-bg-transparent">
            Change
          </button>
        </div>
      );
    }
    return (
      <div className="tw-my-4 tw-max-w-xs">
        <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1">Country of Import (Buyer&apos;s Country)</label>
        <CustomSelect isCountry value={selectedImportCountry} options={countryOptions.countryOfImport} placeholder="Select Country of Import" onChange={handleSelectImportCountry} />
      </div>
    );
  };

  const renderSelectImportCountryStep = () => (
    <div className="tw-pt-4">
      <div className="tw-text-base tw-font-semibold tw-mb-1">Select Country of Import</div>
      <div className="tw-text-sm tw-text-gray-700 tw-mb-4">Select the country you are importing into to find applicable HS codes and tariff rates.</div>
      <div className="tw-max-w-xs">
        <CustomSelect isCountry value={selectedImportCountry} options={countryOptions.countryOfImport} placeholder="Select Country of Import" onChange={handleSelectImportCountry} />
      </div>
    </div>
  );

  const renderHsStep = (showCountryHeader = false) => {
    return (
      <div className="tw-pt-4">
        {showCountryHeader && renderImportCountryHeader()}
        <div className="tw-text-base tw-font-semibold tw-mb-1">Select HS Code</div>
        <div className="tw-text-sm tw-text-gray-700 tw-mb-4 tw-flex tw-items-center tw-gap-1 tw-flex-wrap">
          <span>Our system has classified</span>
          <span className="tw-bg-primary-purple-50 tw-text-primary-purple-700 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium">{keyword || "this product"}</span>
          for <span className="tw-font-bold">{selectedImportCountry?.label || "the selected country"}</span> <span>into following HS Codes. Select one to check applicable tariffs</span>
        </div>

        {hsLoading && (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-10 tw-gap-3 tw-text-sm tw-text-gray-500">
            <div className="tw-flex tw-gap-1.5">
              <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-primary-purple-500 tw-animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-primary-purple-500 tw-animate-bounce" style={{ animationDelay: "150ms" }} />
              <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-primary-purple-500 tw-animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
            Loading HS codes...
          </div>
        )}

        {!hsLoading && hsCodes.length > 0 && (
          <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4 pb-8">
            {hsCodes.map((hs) => {
              const isSelected = selectedHs?.code === hs.code && selectedHs?.description === hs.description;
              return (
                <div key={`${hs.code}-${hs.description}`} className={`tw-bg-white tw-border tw-rounded-xl tw-p-4 tw-shadow-sm tw-cursor-pointer tw-transition tw-flex tw-flex-col ${isSelected ? "tw-border-primary-purple-500 tw-ring-1 tw-ring-primary-purple-200" : "tw-border-gray-200 hover:tw-border-gray-300"}`} onClick={() => handleSelectHs(hs)}>
                  <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                    <div className="tw-text-base tw-font-semibold tw-text-gray-900">HS {hs.code}</div>
                  </div>
                  <div className="tw-text-sm tw-text-gray-700 tw-my-3 tw-flex-1">{hs.description}</div>
                  <div className={`tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium border tw-border-gray-300 tw-shadow-sm px-2 py-1 tw-rounded-lg tw-mt-auto tw-self-start ${isSelected ? "tw-text-primary-purple-600" : "tw-text-gray-700"}`}>
                    <span className={`tw-w-4 tw-h-4 tw-rounded-full tw-border ${isSelected ? "tw-border-primary-purple-500 tw-bg-primary-purple-500" : "tw-border-gray-300"} tw-flex tw-items-center tw-justify-center`}>{isSelected && <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-white" />}</span>
                    {isSelected ? "Selected" : "Select"}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!hsLoading && hasFetchedHs && hsCodes.length === 0 && <div className="tw-text-sm tw-text-gray-500 tw-py-8">No HS codes found for the selected product.</div>}

        {topCountriesLoading && (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-mt-4 tw-gap-3 tw-text-sm tw-text-gray-500">
            <div className="tw-flex tw-gap-1.5">
              <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-primary-purple-500 tw-animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-primary-purple-500 tw-animate-bounce" style={{ animationDelay: "150ms" }} />
              <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-primary-purple-500 tw-animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
            Loading top exporting countries...
          </div>
        )}

        {!topCountriesLoading && topCountries.length > 0 && (
          <div className="tw-my-4">
            <div className="tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-2">Top exporting countries for this HS Code</div>
            <div className="tw-flex tw-flex-wrap tw-gap-2">
              {topCountries
                .filter((country) => country.country_code !== selectedImportCountry?.value)
                .map((country) => (
                  <button
                    key={country.country_code}
                    onClick={() => {
                      setSelectedExportCountries((prev) => {
                        const already = prev.some((c) => c.value === country.country_code);
                        if (already || prev.length >= MAX_EXPORT_COUNTRIES) return prev;
                        return [...prev, { value: country.country_code, label: country.country }];
                      });
                    }}
                    className="tw-text-xs tw-border tw-border-gray-300 tw-rounded-full tw-px-3 tw-py-1 tw-bg-white hover:tw-border-primary-purple-400 hover:tw-text-primary-purple-600 tw-transition tw-capitalize">
                    {country.country}
                  </button>
                ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderCountryStep = () => {
    return (
      <div className="tw-pt-4">
        {renderImportCountryHeader()}

        {renderHsStep()}

        <div className="tw-border-t tw-border-gray-200 tw-mt-6 tw-pt-6">
          <div className="tw-text-base tw-font-semibold tw-mb-1">Select Country of Export</div>
          <div className="tw-text-sm tw-text-gray-700 tw-mb-3">{isImport ? "Select countries you want to import from to compare tariff rates" : "Select the country you are importing from to compare tariff rates"}</div>
          <div className="tw-max-w-md">
            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1">
              Country of Export{" "}
              {selectedExportCountries.length === MAX_EXPORT_COUNTRIES && (
                <svg data-tooltip-id="max-search" data-tooltip-content={`You can select maximum of ${MAX_EXPORT_COUNTRIES} countries at a time`} xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="#101828">
                  <path d="M454-298h52v-230h-52v230Zm25.79-290.46q11.94 0 20.23-8.08 8.29-8.08 8.29-20.02t-8.08-20.23q-8.08-8.28-20.02-8.28T459.98-637q-8.29 8.08-8.29 20.02t8.08 20.23q8.08 8.29 20.02 8.29Zm.55 472.46q-75.11 0-141.48-28.42-66.37-28.42-116.18-78.21-49.81-49.79-78.25-116.09Q116-405.01 116-480.39q0-75.38 28.42-141.25t78.21-115.68q49.79-49.81 116.09-78.25Q405.01-844 480.39-844q75.38 0 141.25 28.42t115.68 78.21q49.81 49.79 78.25 115.85Q844-555.45 844-480.34q0 75.11-28.42 141.48-28.42 66.37-78.21 116.18-49.79 49.81-115.85 78.25Q555.45-116 480.34-116Zm-.34-52q130 0 221-91t91-221q0-130-91-221t-221-91q-130 0-221 91t-91 221q0 130 91 221t221 91Zm0-312Z" />
                </svg>
              )}
              <Tooltip className="trademo-tooltip tw-z-50" place="top" id="max-search" effect="solid" />
            </label>
            <CustomSelect
              maxLimit={MAX_EXPORT_COUNTRIES}
              isCountry
              isMulti
              value={selectedExportCountries}
              options={countryOptions.countryOfExport.filter((c) => c.value !== selectedImportCountry?.value)}
              placeholder="Select country"
              onChange={(value) => {
                setSelectedExportCountries(value || []);
                trademoTrackingEvents(trackingEventDictionary.newMay2026ProductSearchTariffsExportCountriesChanged, { ...generateTrackingObject("product-search", window.location.pathname, currentUserPlan), countries: (value || []).map((c) => c.value) });
              }}
            />
          </div>

          <div className="tw-mt-6">
            <button onClick={fetchDuties} disabled={dutiesLoading || !selectedHs} className="tw-bg-primary-purple-600 disabled:tw-opacity-60 disabled:tw-cursor-not-allowed tw-text-white tw-text-sm tw-font-semibold tw-rounded-lg tw-px-4 tw-py-2 hover:tw-bg-primary-purple-700">
              {dutiesLoading ? "Loading..." : "Confirm Selection to View Tariffs"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderResults = () => {
    const exportLabel = `Imported into ${selectedImportCountry?.label || ""}`;
    const countryCodes = dutiesData ? Object.keys(dutiesData) : [];

    return (
      <div className="tw-pt-4">
        <div className="tw-flex tw-items-center tw-justify-start tw-mb-4">
          <div className="tw-text-sm tw-text-gray-900 tw-font-medium">Tariff Comparison of</div> &nbsp;
          <div className="tw-text-sm tw-text-gray-900 tw-bg-gray-200 tw-px-1 tw-py-1 tw-rounded tw-font-medium">HS {selectedHs?.code}</div>
          <div className="tw-text-sm tw-text-gray-900 tw-font-medium tw-flex tw-items-center tw-gap-1">
            &nbsp;in&nbsp;
            <ReactCountryFlag countryCode={selectedImportCountry?.value} svg />
            {selectedImportCountry?.label}
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            <span className="tw-text-gray-300 tw-pl-2">|</span>
            <button onClick={goToCountryStep} className="tw-text-sm tw-text-primary-purple-500 tw-font-medium hover:tw-underline tw-bg-transparent">
              Change countries
            </button>
          </div>
        </div>

        {dutiesData ? (
          <div className="tw-grid tw-grid-cols-3 tw-gap-4">
            {countryCodes.map((code) => (
              <CountryTariffCard key={code} countryCode={code} countryData={dutiesData[code]} exportLabel={exportLabel} />
            ))}
            {countryCodes.length < MAX_EXPORT_COUNTRIES && <AddCountryCard onClick={goToCountryStep} hsCode={selectedHs?.code} />}
          </div>
        ) : (
          <div className="tw-text-sm tw-text-gray-500">No tariff data available.</div>
        )}
      </div>
    );
  };

  if (hsnClassifierLoading) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-20 tw-gap-4 tw-text-gray-500">
        <div className="tw-h-8 tw-w-8 tw-rounded-full tw-border-2 tw-border-gray-300 tw-border-t-primary-purple-600 tw-animate-spin" />
        <div className="tw-text-sm tw-font-medium">Waiting to fetch HS Code details...</div>
      </div>
    );
  }

  if (hsnClassifierError) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-20 tw-gap-3 tw-text-center">
        <div className="tw-text-base tw-font-semibold tw-text-gray-700">Could not identify a valid product</div>
        <div className="tw-text-sm tw-text-gray-500 tw-max-w-xs">Please search for a valid product name to view tariff details.</div>
      </div>
    );
  }

  if (step === STEPS.RESULTS) return renderResults();
  if (step === STEPS.SELECT_COUNTRY) return renderCountryStep();
  if (step === STEPS.SELECT_HS) return renderHsStep(true);
  return renderSelectImportCountryStep();
};

export default ProductTariffs;
